import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  clearAllCurrencyTransactions,
  queryCurrencyTransactionPage,
  type CurrencyTxRow,
} from '@/client/admin.client';

const PAGE_SIZE = 50;

const ASSET_FILTERS = [
  'all',
  'gold',
  'ruby',
  'emerald',
  'sapphire',
  'stage_ticket',
  'league_ticket',
  'golden_blessing_token',
  'valhalla_ticket',
  'degen_ticket',
  'degen_silver_ticket',
  'degen_golden_ticket',
  'folkvangr_ticket',
  'thor_wrath',
  'blessing_of_freia',
  'glory',
] as const;

type AssetFilter = (typeof ASSET_FILTERS)[number];
type TypeFilter = 'all' | 'mint' | 'burn';

/** 유저 상세 카드용: 해당 계정의 통화 트랜잭션(mint/burn) 감사 로그. 읽기 전용. */
export function CurrencyTransactionHistoryPanel({ account }: { account: string }) {
  const [rows, setRows] = useState<CurrencyTxRow[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assetId, setAssetId] = useState<AssetFilter>('all');
  const [type, setType] = useState<TypeFilter>('all');

  // "전체 삭제" = 계정 무관 GLOBAL 삭제(currencyTransactions + V2, 전 유저). 이 패널은
  // 특정 계정 뷰지만 버튼 동작은 필터링되지 않는다 — 문구/다이얼로그로 명확히 경고한다.
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [clearAllResult, setClearAllResult] = useState<string | null>(null);
  const [clearAllError, setClearAllError] = useState<string | null>(null);

  // account/필터가 바뀌면 in-flight 응답을 폐기하기 위한 요청 토큰. 늦게 도착한
  // 이전 요청(예: 더 보기 도중 필터 변경, 유저 전환)이 다른 조건의 행을 append
  // 하거나 다른 유저 데이터를 잠깐 노출하는 것을 막는다.
  const requestRef = useRef(0);
  const cursorRef = useRef<string | null>(null);

  const fetchPage = useCallback(
    async (reset: boolean) => {
      const token = ++requestRef.current;
      setLoading(true);
      setError(null);
      try {
        const res = await queryCurrencyTransactionPage({
          account,
          assetId: assetId === 'all' ? undefined : assetId,
          type: type === 'all' ? undefined : type,
          cursor: reset ? null : cursorRef.current,
          limit: PAGE_SIZE,
        });
        if (token !== requestRef.current) return; // stale 응답 폐기
        cursorRef.current = res.nextCursor;
        setRows((prev) => (reset ? res.records : [...prev, ...res.records]));
        setTotal(res.total);
        setHasMore(res.hasMore);
      } catch (e) {
        if (token !== requestRef.current) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (token === requestRef.current) setLoading(false);
      }
    },
    [account, assetId, type]
  );

  // account / 필터 변경 시 첫 페이지부터 재조회.
  useEffect(() => {
    cursorRef.current = null;
    void fetchPage(true);
  }, [fetchPage]);

  const handleConfirmClearAll = async () => {
    setClearingAll(true);
    setClearAllError(null);
    try {
      const result = await clearAllCurrencyTransactions();
      setClearAllResult(`삭제 완료: ${result.cleared.join(', ')}`);
      setShowClearAllConfirm(false);
      cursorRef.current = null;
      await fetchPage(true);
    } catch (e) {
      setClearAllError(e instanceof Error ? e.message : String(e));
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">Currency Transactions</h3>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            에셋·타입 필터는 동시 적용 불가 (하나만)
          </span>
          <Select
            value={assetId}
            onValueChange={(v) => {
              setAssetId(v as AssetFilter);
              if (v !== 'all') setType('all');
            }}
          >
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSET_FILTERS.map((a) => (
                <SelectItem key={a} value={a} className="text-xs">
                  {a === 'all' ? '전체 에셋' : a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={type}
            onValueChange={(v) => {
              setType(v as TypeFilter);
              if (v !== 'all') setAssetId('all');
            }}
          >
            <SelectTrigger className="h-8 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                전체
              </SelectItem>
              <SelectItem value="mint" className="text-xs">
                mint
              </SelectItem>
              <SelectItem value="burn" className="text-xs">
                burn
              </SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="secondary"
            disabled={loading}
            onClick={() => void fetchPage(true)}
          >
            {loading ? 'Loading…' : 'Reload'}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="ml-1 border-l border-border pl-2.5"
            disabled={clearingAll}
            onClick={() => {
              setClearAllError(null);
              setClearAllResult(null);
              setShowClearAllConfirm(true);
            }}
          >
            커런시 트랜잭션 전체 삭제
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {clearAllError && (
        <Alert variant="destructive">
          <AlertDescription>{clearAllError}</AlertDescription>
        </Alert>
      )}

      {clearAllResult && (
        <Alert>
          <AlertDescription>{clearAllResult}</AlertDescription>
        </Alert>
      )}

      <div className="text-xs text-muted-foreground">total: {total}</div>
      <div className="max-h-96 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Memo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.__id ?? `${r.timestamp}-${r.assetId}-${r.amount}-${r.reason}`}>
                <TableCell className="whitespace-nowrap text-xs">
                  {new Date(r.timestamp).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Badge variant={r.type === 'mint' ? 'secondary' : 'outline'}>{r.type}</Badge>
                </TableCell>
                <TableCell className="text-xs">{r.assetId}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {r.type === 'burn' ? '-' : '+'}
                  {r.amount}
                </TableCell>
                <TableCell className="text-xs">{r.reason}</TableCell>
                <TableCell className="break-all text-xs text-muted-foreground">
                  {r.memo ?? ''}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  No transactions
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {hasMore && (
        <Button
          size="sm"
          variant="outline"
          disabled={loading}
          onClick={() => void fetchPage(false)}
        >
          더 보기 ({rows.length}/{total})
        </Button>
      )}

      {/* 전체 삭제 확인 — 이 다이얼로그의 동작 범위는 GLOBAL(전 유저)이지 이 패널의
          account 하나가 아니다. 문구로 명시해 오인을 막는다. */}
      <AlertDialog
        open={showClearAllConfirm}
        onOpenChange={(open) => {
          if (!open && !clearingAll) setShowClearAllConfirm(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              전체 커런시 트랜잭션 삭제
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="text-destructive">
                모든 유저의 커런시 트랜잭션(currencyTransactions + V2)이 전부 삭제됩니다. 이
                유저만이 아니라 전체입니다. staging/dev 에서만 동작하며 되돌릴 수 없습니다.
                계속할까요?
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearingAll}>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmClearAll();
              }}
              disabled={clearingAll}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {clearingAll ? '삭제 중…' : '전체 삭제'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CurrencyTransactionHistoryPanel;
